// Library
import { useState, useEffect, useCallback } from "react";
import React from 'react';
import {
  useTheme,
  Box,
  Button,
  TextField,
  Typography,
  ButtonGroup,
  IconButton,
  Popover,
  Paper,
  Dialog,
  Stack
} from "@mui/material";
import { Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { LocalizationProvider, DatePicker, DateField } from '@mui/x-date-pickers';
import { lighten } from "@mui/material/styles";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useNavigate } from "react-router-dom";
import useMediaQuery from "@mui/material/useMediaQuery";
import axios from "axios";
import { useTranslation } from "react-i18next";
import "../../i18n";
import { de } from 'date-fns/locale';
import { format, addDays } from 'date-fns';

// Components
import TitleHeader from "../../components/TitleHeader"; 
import Loading from "../../components/Loading";
import useToast from "../../components/useToast";
import UnsavedChangesNotice from "../../components/UnsavedChangesNotice";
import { tokens } from "../../theme";

// Icons
import Alert from "@mui/material/Alert";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import LibraryAddIcon from "@mui/icons-material/LibraryAdd";

//Others
import { API_BASE_URL } from "../../config";

const predefinedColors = [
  "#FFEBEE", // Light red
  "#E3F2FD", // Light blue
  "#E8F5E9", // Light green
  "#FFF3E0", // Light orange
  "#F3E5F5", // Light purple
  "#FFCDD2", // Another light red variant
  "#C8E6C9", // Another light green variant
];

// Robust "is closed" check. Adjust if your API uses another flag.
const isDayClosed = (closingDays, idx) => closingDays?.[idx] === 0;

// Big, centered “closed” card for the column body
const ClosedCard = ({ label = "Geschlossen" }) => (
  <Box
    sx={{
      width: "100%",
      height: "100%",
      minHeight: 140,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
      gap: 1,
      border: "1px dashed",
      borderColor: "divider",
      borderRadius: 1.5,
      bgcolor: (t) => (t.palette.mode === "dark" ? "action.hover" : "grey.50"),
      backgroundImage:
        "repeating-linear-gradient(-45deg, rgba(0,0,0,0.04) 0, rgba(0,0,0,0.04) 8px, transparent 8px, transparent 16px)",
      color: "text.secondary",
      textAlign: "center",
      p: 2,
    }}
  >
    <LockOutlinedIcon />
    <Typography variant="body2">{label}</Typography>
  </Box>
);


const TimeReq = ({ timereq }) => {
  const theme = useTheme();
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [showMissingNotification, setShowMissingNotification] = useState(false);
  const [timereqData, setTimeReqData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [openingHours, setOpeningHours] = useState([]);
  const [closingHours, setClosingHours] = useState([]);
  const [closingDays, setClosingDays] = useState([]);
  const [startBreak, setStartBreak] = useState([]);
  const [endBreak, setEndBreak] = useState([]);
  const [weekAdjustment, setWeekAdjustment] = useState(0);
  const [slotDuration, setSlotDuration] = useState(0);
  const token = localStorage.getItem("session_token");
  const [employeeCount, setEmployeeCount] = useState("");
  const [selectedButtons, setSelectedButtons] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [slotEmployeeCounts, setSlotEmployeeCounts] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [deptSelectOpen, setDeptSelectOpen] = useState(false);
  const [shiftPlanning, setShiftPlanning] = useState("");
  const navigate = useNavigate();
  const [anchorElOptions, setAnchorElOptions] = useState(null);
  const [department_list, setDepartmentList] = useState([]);
  const [shiftList, setShiftList] = useState([]);
  const [shiftValues, setShiftValues] = useState({});
  const [shiftColors, setShiftColors] = useState({});
  const { t, i18n } = useTranslation();
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [templateNameList, setTemplateNameList] = useState([]);
  const { success, error, info, loading, close } = useToast();
  const [hasLocalAppliedChanges, setHasLocalAppliedChanges] = useState(false);

  const themeColors = tokens(theme.palette.mode);

  const getGreenShade = (employeeCount) => {
    const maxIndex = themeColors.timereq.length - 1;
    const index = Math.min(employeeCount, maxIndex);
    return themeColors.timereq[index];
  };

  const handleDateChange = (date) => {
    // Close DatePicker
    setDatePickerOpen(false);
  
    // Find the selected Monday
    const dayOfWeek = date.getDay();
    const selectedMonday = new Date(date);
    selectedMonday.setDate(selectedMonday.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
  
    // Assuming you store the current Monday based on `weekAdjustment` in state or can calculate it
    const currentMonday = new Date(timereqData.week_start);
  
    // Calculate the difference in weeks between the selected Monday and the current Monday
    const differenceInWeeks = Math.round((selectedMonday - currentMonday) / ( 24 * 60 * 60 * 1000));
  
    // Update weekAdjustment accordingly
    setWeekAdjustment(weekAdjustment + differenceInWeeks);
    setSelectedTemplate("");
  };

  const convertTimeToMinutes = (timeStr) => {
    if (!timeStr) return undefined;
    const [hour, minute] = timeStr.split(":");
    return parseInt(hour) * 60 + parseInt(minute);
  };

  const formatTimeFromMinutes = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  // Helper function to check if the current time is within the operating hours and break time
  const isTimeWithinRange = (
    current,
    opening,
    startBreak,
    endBreak,
    closing
  ) => {
    if (current === undefined || current === null) {
      return false;
    } else {
      if (closing < opening || startBreak < opening) {
        if (closing === null || closing === undefined || isNaN(closing)) {
          return current >= opening || current < startBreak;
        } else {
          return (
            (current >= opening && current < startBreak) ||
            (current >= endBreak && current <= 1440) ||
            current < closing
          );
        }
      } else {
        if (closing === null || closing === undefined) {
          return current >= opening && current <= startBreak;
        }
        return (
          (current >= opening && current < startBreak) ||
          (current >= endBreak && current <= closing)
        );
      }
    }
  };

  /// Define Click and Drag Function
  useEffect(() => {
    const endDrag = () => setIsDragging(false);
    window.addEventListener("mouseup", endDrag);

    return () => {
      window.removeEventListener("mouseup", endDrag);
    };
  }, []);

  const handleMouseDown = useCallback((colIndex, btnIndex) => {
    setIsDragging(true);
    toggleButtonSelection(colIndex, btnIndex);
  }, []);

  const toggleButtonSelection = (columnIndex, btnIndex) => {
    const selectedKey = `${columnIndex}-${btnIndex}`;
    setSelectedButtons((prevSelectedButtons) => {
      if (prevSelectedButtons.includes(selectedKey)) {
        return prevSelectedButtons.filter((key) => key !== selectedKey);
      } else {
        return [...prevSelectedButtons, selectedKey];
      }
    });
  };

  const setEmployees = (e) => {
    const raw = e.target.value;
    if (raw === "") return setEmployeeCount("");
    const n = Math.max(0, Math.floor(Number(raw)));
    if (Number.isNaN(n)) return;
    setEmployeeCount(String(n));
  };


  const EnteredSlots = () => {
    // Muss explizit 0 oder eine positive Zahl sein:
    if (employeeCount === "" || Number.isNaN(Number(employeeCount))) {
      error(t("planning.enter_number_first") || "Bitte 0 oder eine positive Zahl eingeben.");
      return;
    }

    const count = Math.max(0, Number(employeeCount)); // 0 = Slots „zurücksetzen“
    const updatedCounts = { ...(slotEmployeeCounts || {}) };

    selectedButtons.forEach((slot) => {
      updatedCounts[slot] = count;
    });

    setSlotEmployeeCounts(updatedCounts);
    setSelectedButtons([]);
    setEmployeeCount(""); // Eingabefeld wieder leeren
    setHasLocalAppliedChanges(true);
  };



  useEffect(() => {
    const fetchTimeReqData = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/api/requirement/workforce`, {
          params: {
            week_adjustment: weekAdjustment,
            selectedDepartment: selectedDepartment,
            selectedTemplate: selectedTemplate,
          },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = response.data;
        setTimeReqData(response.data);
        setDepartmentList(response.data.department_list);
        let filteredShifts = response.data.shift_list;
        if (selectedDepartment) {
          filteredShifts = response.data.shift_list.filter((shift) => {
            // Check if the shift belongs to the selected department
            // or if it has no department assigned (null or "None")
            return shift[4] === selectedDepartment || shift[4] === null || shift[4] === null;
          });
        }
        setShiftList(filteredShifts);
        const colors = {};
        response.data.shift_list.forEach((shift, index) => {
          const baseColor = predefinedColors[index % predefinedColors.length];
          colors[shift] = {
            base: baseColor,
            lighter: lighten(baseColor, 0.6),  // 0 = same, 1 = white → 0.4 = clearly brighter
          };
        });
        setShiftColors(colors);
        if (data.shift_data) {
          setShiftValues(data.shift_data);
        } else {
          setShiftValues({});
        }
        setSlotDuration(data.minutes);
        setTemplateNameList(data.timereq_template_names);
        setShiftPlanning(data.shift_planning);

        // Slot-Counts direkt aus dem Server-Stand übernehmen
        setSlotEmployeeCounts(data.timereq_dict || {});

        // Öffnungszeiten & Pausen aus data.opening_dict zusammensetzen
        const openingHoursArr = [];
        const startBreakArr = [];
        const endBreakArr = [];
        const closingHoursArr = [];

        for (let i = 0; i < data.day_num; i++) {
          openingHoursArr.push(data.opening_dict[`${i + 1}&0`]);
          startBreakArr.push(data.opening_dict[`${i + 1}&1`]);
          endBreakArr.push(data.opening_dict[`${i + 1}&2`]);
          closingHoursArr.push(data.opening_dict[`${i + 1}&3`]);
        }

        setOpeningHours(openingHoursArr);
        setClosingDays(data.closing_days);
        setStartBreak(startBreakArr);
        setEndBreak(endBreakArr);
        setClosingHours(closingHoursArr);

        // Falls Vorlage gewählt → anwenden und als lokale Änderung markieren
        let applied = false;
        if (selectedTemplate) {
          if (data.shift_planning === 0) {
            applyTemplate(data);                   // setzt intern bereits das Flag
          } else if (data.shift_planning === 1) {
            applyTemplateForShiftPlanning1(data);  // setzt intern bereits das Flag
          }
          applied = true;
        }

        setIsLoading(false);
        setHasLocalAppliedChanges(applied);
        } catch (error) {
          setIsLoading(false);
          // Wichtig: Flag hier NICHT auf false zurücksetzen,
          // damit lokale (nicht gespeicherte) Änderungen nicht verloren gehen.
          if (error?.response?.status === 401 || error?.response?.status === 500) {
            navigate("/login");
          }
        }
    };

    fetchTimeReqData();
  }, [weekAdjustment, token, selectedDepartment, selectedTemplate]);

  useEffect(() => {
    // Check if there's exactly one department in the list
    if (department_list.length === 1) {
      setSelectedDepartment(department_list[0]);
    } 
  }, [department_list]);

  const goToNextWeek = () => {
    setWeekAdjustment(weekAdjustment + 7);
    setSelectedTemplate("");
  };

  const goToPreviousWeek = () => {
    setWeekAdjustment(weekAdjustment - 7);
    setSelectedTemplate("");
  };

  const unsavedChanges = hasLocalAppliedChanges;

  const applyTemplate = (data) => {
    // Basis = Server-Stand dieser Woche (verhindert Überschreiben durch alte States)
    const updatedCounts = { ...(data.timereq_dict || {}) };

    for (let columnIndex = 0; columnIndex < data.day_num; columnIndex++) {
      const openingTimeMinutes    = convertTimeToMinutes(data.opening_dict[`${columnIndex + 1}&0`]);
      const startBreakTimeMinutes = convertTimeToMinutes(data.opening_dict[`${columnIndex + 1}&1`]) - 1;
      const endBreakTimeMinutes   = convertTimeToMinutes(data.opening_dict[`${columnIndex + 1}&2`]);
      const closingTimeMinutes    = convertTimeToMinutes(data.opening_dict[`${columnIndex + 1}&3`]) - 1;

      for (let btnIndex = 0; btnIndex < data.daily_slots; btnIndex++) {
        const currentTimeMinutes = convertTimeToMinutes(data.slots_dict[btnIndex]);
        if (isTimeWithinRange(currentTimeMinutes, openingTimeMinutes, startBreakTimeMinutes, endBreakTimeMinutes, closingTimeMinutes)) {
          const key = `${columnIndex}-${btnIndex}`;
          updatedCounts[key] = data.template_dict[key];
        }
      }
    }

    setSlotEmployeeCounts(updatedCounts);
    setHasLocalAppliedChanges(true); // Indikator einschalten
  };



  const applyTemplateForShiftPlanning1 = (timereqData) => {
    const updatedShiftValues = { ...shiftValues };
    shiftList.forEach((shift) => {
      for (let columnIndex = 0; columnIndex < timereqData.day_num; columnIndex++) {
        const key = `${shift[1]}_${columnIndex}`;
        updatedShiftValues[key] = timereqData.template_dict[key];
      }
    });
    setShiftValues(updatedShiftValues);
    setHasLocalAppliedChanges(true); // Indikator einschalten
  };

  

  const handleFormSubmit = async (buttonName) => {
    setIsLoading(true);

    if (!selectedDepartment) {
      error(t("notification.error"));
      setIsLoading(false);
      return; 
    }

    // NEU: Bei Shift-Planning 0 blocken, wenn noch Slots markiert, aber nicht angewendet sind
    if (shiftPlanning === 0 && selectedButtons.length > 0) {
      error(
        t("planning.apply_selection_first") ||
        "Du hast Slots ausgewählt, aber die Anzahl nicht angewendet. Bitte zuerst mit dem kleinen Speichern-Button übernehmen."
      );
      setIsLoading(false);
      return;
    }

    try {
      const payload = {};
      Object.entries(slotEmployeeCounts || {}).forEach(([key, count]) => {
        if (count !== undefined) {
          const [columnIndex, btnIndex] = key.split("-");
          const dayNum = columnIndex;
          const currentTime = timereqData.slots_dict[parseInt(btnIndex)];
          payload[`worker_${dayNum}_${currentTime}`] = String(count);
        }
      });
      payload.button = buttonName;
      payload.template_name = selectedTemplate;
      payload.department = selectedDepartment;
      payload.shifts = shiftValues;


      // console.log(payload)
      // Send the updated form values to the server for database update
      // console.log("Final payload before sending to server:", payload);
      await axios.post(
        `${API_BASE_URL}/api/requirement/workforce?week_adjustment=${weekAdjustment}&selectedDepartment=${encodeURIComponent(
          selectedDepartment
        )}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      success(t("notification.successful_planning"));
      setHasLocalAppliedChanges(false);
      setIsLoading(false);
      // console.log("Sending this data to server:", payload);
    } catch (error) {
      error(t("notification.error"));
      setIsLoading(false);
    }
  };

  const openOptions = Boolean(anchorElOptions);
  const idOptions = openOptions ? 'simple-popover-options' : undefined;

  const handleOptionsClick = (event) => {
    setAnchorElOptions(event.currentTarget);
  };

  const handleOptionsClose = () => {
    setAnchorElOptions(null);
  };

  const handleShiftChange = (index, value) => {
    const newValue = (value === "" || value === "0") ? 0 : value;
  
    setShiftValues((prev) => ({ ...prev, [index]: newValue }));
    setHasLocalAppliedChanges(true);
  };

  const handleDepartmentChange = (e) => {
    const dept = e.target.value;
  
    // 1) switch department
    setSelectedDepartment(dept);
  
    // 2) clear template selection
    setSelectedTemplate("");
  
    // (recommended) clear interaction state so you don’t carry over selections/flags
    setSelectedButtons([]);
    setEmployeeCount("");
    setHasLocalAppliedChanges(false);
  };

  const handleReset = () => {
    setSlotEmployeeCounts('');
    setShiftValues('');
    setHasLocalAppliedChanges(false);
    setAnchorElOptions(null);
  };

  const actions = (
    <Box display="flex" alignItems="center" gap={1}>
      {department_list.length > 1 && (
      <FormControl variant="outlined" size="small" sx={{ minWidth: 220 }}>
        <InputLabel id="department-label">
          {t("planning.department")}
        </InputLabel>
        <Select
          labelId="department-label"
          value={selectedDepartment}
          onChange={(e) => {
            handleDepartmentChange(e);
            // optional: close after selecting
            setDeptSelectOpen(false);
          }}
          label={t("planning.department")}
          open={deptSelectOpen}
          onOpen={() => setDeptSelectOpen(true)}
          onClose={() => setDeptSelectOpen(false)}
          MenuProps={{ 
            disablePortal: true,
            MenuListProps: {
            },
            PaperProps: { sx: { bgcolor: "background.paper" } } }}
        >
          <MenuItem value="">{t("planning.choosedepartment")}</MenuItem>
          {department_list.map((department) => (
            <MenuItem key={department} value={department}>
              {department}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      )}
      <IconButton
        aria-describedby={idOptions}
        onClick={handleOptionsClick}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1.5,
          bgcolor: "background.paper",
        }}
      >
        <MoreVertIcon/>
      </IconButton>
    </Box>
  );


  return (
    <Box m={2}>
      <Loading open={isLoading} />
      <TitleHeader
        title={t("planning.title")}
        subtitle={t("planning.subtitle")}
        actions={actions}
      />

      {/* Popover for Options */}
      <Popover
        id={idOptions}
        open={openOptions}
        anchorEl={anchorElOptions}
        onClose={handleOptionsClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{
          sx: (theme) => ({
            p: 1,
            maxWidth: 360,
            "& .MuiButton-root": {
              color: theme.palette.text.primary,
              textTransform: "none",
              justifyContent: "flex-start",
              fontSize: 12,
              fontWeight: theme.typography.fontWeightRegular,
            },
          }),
        }}
      >
        <Box p={1}>
          <Stack direction="column" spacing={0.5} sx={{ minWidth: 240 }}>
            <Button
              onClick={() => navigate("/create/planning/template")}
              startIcon={<LibraryAddIcon fontSize="small" />}
              sx={{ justifyContent: "flex-start" }}
            >
              {t("planning.create_template")}
            </Button>

            <Button
              onClick={handleReset}
              startIcon={<RestartAltIcon fontSize="small" />}
              sx={{ justifyContent: "flex-start" }}
            >
              {t("planning.reset_data")}
            </Button>
          </Stack>
        </Box>
      </Popover>

      {!isLoading && !selectedDepartment && department_list.length > 1 ? (
        <Paper
          elevation={5}
          sx={{ p: 4, bgcolor: "background.box", borderRadius: 1, mt: 2 }}
        >
          <Box sx={{ minHeight: "45vh", display: "grid", placeItems: "center" }}>
            <Box textAlign="center">
              <InfoOutlinedIcon sx={{ fontSize: 48, mb: 1, color: "text.secondary" }} />
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {t("planning.select_department_hint")}
              </Typography>
            </Box>
          </Box>
        </Paper>
      ) : (
        <>
      {selectedDepartment && (
        <>
      <Paper
        elevation={5}
        sx={{
          p: 2,
          bgcolor: "background.box",
          //border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{
            maxHeight: 'calc(80vh - 80px)', 
            overflowY: 'auto',
            p: 2,
            bgcolor: "background.box",
            borderRadius: 1,
          }}
        >

      {selectedDepartment && (
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            mb: 2,
            background: "white",
            borderRadius: 1,
            border: "1px solid #e0e0e0",
            boxShadow: "0px 0px 10px rgba(0,0,0,0.08)",
            p: 1,
          }}
        >
          {/* Left: Template select + info */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, m: 1 }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="template-select-label">{t("planning.select_template")}</InputLabel>
              <Select
                labelId="template-select-label"
                label={t("planning.select_template")}
                value={selectedTemplate}
                onChange={(e) => {
                  if (e.target.value !== "no_template") setSelectedTemplate(e.target.value);
                }}
                MenuProps={{ PaperProps: { sx: { bgcolor: "background.paper" } } }}
              >
                {templateNameList.length > 0 ? (
                  templateNameList.map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="no_template" disabled>
                    {t("planning.no_template_defined")}
                  </MenuItem>
                )}
              </Select>
            </FormControl>
              <Tooltip
                title={
                  <div style={{ fontSize: 14 }}>
                    <strong>{t("info.title_template")}</strong>
                    <br /><br />
                    {t("info.title_template_step1")}<br />
                    {t("info.title_template_step2")}<br />
                    {t("info.title_template_step3")}
                  </div>
                }
              >
                <InfoOutlinedIcon />
              </Tooltip>

          </Box>

          {/* Center: week nav + monday picker */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, m: 1, flex: 1, justifyContent: "center" }}>
            <IconButton onClick={goToPreviousWeek}>
              <ChevronLeft style={{ color: "#2E2E2E" }} />
            </IconButton>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
              <DatePicker
                value={new Date(timereqData.week_start)}
                onChange={handleDateChange}
                label={t("planning.monday")}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: { bgcolor: "background.paper" },
                  },
                }}
              />
            </LocalizationProvider>
            <IconButton onClick={goToNextWeek}>
              <ChevronRight style={{ color: "#2E2E2E" }} />
            </IconButton>
          </Box>

          {/* Right: actions */}
          {isNonMobile && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, m: 1 }}>
              <UnsavedChangesNotice visible={unsavedChanges} />
              <Button
                onClick={() => handleFormSubmit("Submit")}
                color="primary"
                variant="contained"
                disabled={
                  !selectedDepartment ||
                  (shiftPlanning === 0 && selectedButtons.length > 0)
                }
              >
                {t("planning.submit")}
              </Button>
            </Box>
          )}
        </Box>
      )}


      
      {(selectedDepartment || department_list.length === 1) && (
        <>
      {shiftPlanning === 0 && (
        // Neuer Wrapper: stellt den Indikator RECHTS NEBEN die weiße Box
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          {/* Deine unveränderte weiße Box (Größe/Stil bleiben gleich) */}
          <Box
            display="grid"
            gap="20px"
            gridTemplateColumns={isMobile ? "repeat(6, minmax(0, 1fr))" : "repeat(7, minmax(0, 1fr))"}
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: "center",
              justifyContent: "space-between",
              width: "30%",
              background: "white",
              borderRadius: 1,
              border: "1px solid #e0e0e0",
              boxShadow: "0px 0px 10px rgba(0,0,0,0.08)",
              p: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", ml: "10px", width: "100%" }}>
              <TextField
                type="number"
                value={employeeCount || ""}
                onChange={(e) => setEmployees(e)}
                placeholder={t("planning.enteremployeecount")}
                variant="outlined"
                sx={{ mr: "10px", width: 250 }}
                InputProps={{ style: { height: 35 } }}
                inputProps={{ min: 0, style: { padding: "10px 14px", backgroundColor: "white" } }}
              />

              <Button
                onClick={EnteredSlots}
                type="submit"
                color="primary"
                variant="contained"
                disabled={
                  selectedButtons.length === 0 ||
                  employeeCount === "" ||
                  Number.isNaN(Number(employeeCount)) ||
                  Number(employeeCount) < 0
                }
              >
                {t("button.save_2")}
              </Button>
            </Box>
          </Box>

        </Box>
      )}
      </>
      )}
      
      <span></span>

      {(shiftPlanning === 0 && selectedDepartment) && (
      <Box
        sx={{
          maxHeight: 'calc(75vh - 75px)',
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)", // 7 columns for each day
          gap: theme.spacing(1), // Reduce the gap if it's too large
          marginBottom: "1rem",
          background: "white",
          borderRadius: "8px",
          border: "1px solid #e0e0e0",
          boxShadow: "0px 0px 10px rgba(0,0,0,0.2)",
          "& > :first-of-type": { // Apply styles to the first child only
            marginLeft: "10px",
          },
          "& > :last-of-type": { // Apply styles to the last child only
            marginRight: "10px",
          }
        }}
      >
      {Array.from({ length: timereqData.day_num }).map((_, columnIndex) => {
        // Berechnen des Datums für jeden Tag basierend auf dem Startdatum und columnIndex
        const dateForDay = addDays(new Date(timereqData.week_start), columnIndex);
        const closed = isDayClosed(closingDays, columnIndex);
        const closedLabel = t("availabilty.closed") ?? "Geschlossen";

        return (
          <Box
            key={`column-${columnIndex}`}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              marginTop: "5px",
            }}
          >
            <Typography 
              variant="h5" // Slightly smaller font for the weekday
              component="div"
              sx={{
                fontWeight: 'bold', // Makes the text bold
              }}
            >
              {format(dateForDay, 'EEEE', { locale: de })} 
            </Typography>

            {/* Date */}
            <Typography 
              variant="subtitle2" // Smaller font size
              component="div"
              sx={{
                color: 'gray', // Gray text color
                fontStyle: 'italic', // Optional: Makes the text italic
              }}
            >
              {format(dateForDay, 'dd.MM.yyyy', { locale: de })}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "100%", // Full width
                maxHeight: 'calc(55vh - 55px)',
                overflowY: "auto", // To allow scrolling if the content exceeds the fixed height
                marginTop: "10px",
                marginBottom: "10px",
                background: "white"
              }}
            >
              {closed ? (
                <ClosedCard label={closedLabel} />
              ) : (
              Array.from({ length: timereqData.daily_slots }).map(
                (_, btnIndex) => {
                  const timereqKey = `${columnIndex}-${btnIndex}`;
                  const currentTimeMinutes = convertTimeToMinutes(
                    timereqData.slots_dict[btnIndex]
                  );
                  const openingTimeMinutes = convertTimeToMinutes(
                    openingHours[columnIndex]
                  );
                  const startBreakTimeMinutes =
                    convertTimeToMinutes(startBreak[columnIndex]) - 1;
                  const endBreakTimeMinutes = convertTimeToMinutes(
                    endBreak[columnIndex]
                  );
                  const closingTimeMinutes =
                    convertTimeToMinutes(closingHours[columnIndex]) - 1;

                  const startTimeMinutes = convertTimeToMinutes(timereqData.slots_dict[btnIndex]);
                  const endTimeMinutes = startTimeMinutes + slotDuration;
                  const startTimeFormatted = formatTimeFromMinutes(startTimeMinutes);
                  const endTimeFormatted = formatTimeFromMinutes(endTimeMinutes);

                  if (
                    isTimeWithinRange(
                      currentTimeMinutes,
                      openingTimeMinutes,
                      startBreakTimeMinutes,
                      endBreakTimeMinutes,
                      closingTimeMinutes
                    )
                  ) {
                    const isSelected = selectedButtons.includes(
                      `${columnIndex}-${btnIndex}`
                    );
                    const employeeCountForThisSlot =
                      slotEmployeeCounts[`${columnIndex}-${btnIndex}`] || "";

                    const buttonStyle = {
                      variant: "outlined",
                      color: "secondary",
                      style: {
                        width: "100%",
                        borderColor: "lightgrey",
                        "&:hover": {
                          borderColor: "white",
                        }
                      }
                    };

                    if (isSelected) {
                      buttonStyle.variant = "contained";
                      buttonStyle.color = "secondary";
                    } else if (employeeCountForThisSlot > 0) {
                      buttonStyle.variant = "contained";
                      buttonStyle.style.backgroundColor = getGreenShade(employeeCountForThisSlot);
                      buttonStyle.style.color = "white";
                    }

                    return (
                      <Button
                        key={`btn-${columnIndex}-${btnIndex}`}
                        {...buttonStyle}
                        onMouseDown={() => handleMouseDown(columnIndex, btnIndex)}
                        onMouseOver={() => {
                          if (isDragging) {
                            toggleButtonSelection(columnIndex, btnIndex);
                          }
                        }}
                        onMouseUp={() => setIsDragging(false)}
                      >
                        {`${startTimeFormatted} - ${endTimeFormatted}`}
                        {employeeCountForThisSlot && (
                          <>
                            <span style={{ margin: '0 10px' }}>|</span> 
                            <span style={{ marginLeft: '10px' }}>{employeeCountForThisSlot}</span>
                          </>
                        )}
                      </Button>
                    );
                  }
                  return null; // Return null if the current time is outside the opening and closing hours
                }
              ))}
            </Box>
          </Box>
        );
        })}
      </Box>
      )}
      {(selectedDepartment && shiftPlanning === 1) && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr repeat(7, 1fr)", // 1 column for shift name + 7 columns for weekdays
            gap: theme.spacing(1),
            padding: "10px",
            background: "white",
            borderRadius: "8px",
            border: "1px solid #e0e0e0",
            boxShadow: "0px 0px 10px rgba(0,0,0,0.2)",
            overflow: 'auto',
          }}
        >
          {/* Header Row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "left",
              padding: "10px",
              marginLeft: "5px",
              marginRight: "5px",
              marginTop: "5px",
              fontWeight: "bold",
              backgroundColor: "#f5f5f5",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              {t("planning.shift_name")}
            </Typography>
          </Box>
          {Array.from({ length: timereqData.day_num }).map((_, columnIndex) => {
            const dateForDay = addDays(new Date(timereqData.week_start), columnIndex);
            return (
              <Box
                key={`header-column-${columnIndex}`}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  padding: "10px",
                  marginLeft: "5px",
                  marginRight: "5px",
                  marginTop: "5px",
                  backgroundColor: "#f5f5f5",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  {format(dateForDay, "EEEE", { locale: de })}
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{ color: "gray", fontStyle: "italic" }}
                >
                  {format(dateForDay, "dd.MM.yyyy", { locale: de })}
                </Typography>
              </Box>
            );
          })}

          {/* Rows for Shift Data */}
          {shiftList.map((shift, shiftIndex) => (
            <React.Fragment key={`shift-row-${shiftIndex}`}>
              {/* First Column: Shift Name */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  justifyContent: "left",
                  padding: "10px",
                  marginLeft: "5px",
                  marginRight: "5px",
                  marginTop: "5px",
                  fontWeight: "bold",
                  backgroundColor: shiftColors[shift]?.base || "white",
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  {shift[1]}
                </Typography>
                <Typography variant="subtitle2" sx={{ fontStyle: "italic", color: "gray" }}>
                  {shift[2]}
                </Typography>
              </Box>

              {/* Columns for Each Day */}
              {Array.from({ length: timereqData.day_num }).map((_, columnIndex) => {
                const isClosed = closingDays[columnIndex] === 0;
                const valueKey = `${shift[1]}_${columnIndex}`;
                const rawValue = shiftValues[valueKey];
                const hasValue =
                  rawValue !== undefined &&
                  rawValue !== null &&
                  rawValue !== "" &&
                  Number(rawValue) > 0;

                  const baseFieldColor = "#ffffff";
                  const filledFieldColor = shiftColors[shift]?.lighter || "#ffffff";  // row background

                return(
                  <Box
                    key={`shift-${shiftIndex}-day-${columnIndex}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "5px",
                      marginLeft: "5px",
                      marginRight: "5px",
                      marginTop: "5px",
                      borderBottom: "1px solid #e0e0e0",
                      backgroundColor: shiftColors[shift]?.base || "white",
                    }}
                  >
                  {isClosed ? (
                    <Box
                      sx={{
                        width: "100%",
                        backgroundColor: "#ffffff",
                        padding: "6px",
                        textAlign: "center",
                        borderRadius: "8px",
                        border: "1px solid #e0e0e0",
                        display: "flex",
                      }}
                    >
                      <LockOutlinedIcon />
                      <Typography variant="subtitle1" sx={{ color: "black" }}>
                      {t("availabilty.closed")}
                      </Typography>
                    </Box>
                  ) : (
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    type="number"
                    placeholder={t("planning.shift_label")}
                    value={rawValue || ""}
                    onChange={(e) =>
                      handleShiftChange(`${shift[1]}_${columnIndex}`, e.target.value)
                    }
                    onKeyDown={(e) => {
                      // block minus, plus, decimals, exponent, and whitespace
                      if (["-", "+", ".", ","].includes(e.key)) e.preventDefault();
                    }}
                    inputProps={{ min: 0, step: 1 }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: hasValue ? filledFieldColor : baseFieldColor,
                        "&:hover": {
                          backgroundColor: hasValue ? filledFieldColor : baseFieldColor,
                        },
                        "&.Mui-focused": {
                          backgroundColor: hasValue ? filledFieldColor : baseFieldColor,
                        },
                        // 🔹 center the text
                        "& .MuiOutlinedInput-input": {
                          textAlign: "center",
                          padding: "8px 0",
                        },
                      },
                    }}
                  />
                  )}
                </Box>
                );
              })}
            </React.Fragment>
          ))}
      </Box>
      )}
      </Box>
      </Paper>
      </>
      )}
      </>
      )}
    </Box>
  
  );
};

export default TimeReq;
